import styled, { css } from 'styled-components';
import { Text } from '~/components/ui/Text';
import bg from '../../assets/images/case_item_bg.png';
import { Spinner } from '../Spinner';
import { CaseType, ICaseTagStyled } from './CaseItem.model';

const StyledTopContainer = styled.div<{ $isMultiple?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: ${(props) => (props.$isMultiple ? 'column' : 'row')};
	justify-content: ${(props) => !props.$isMultiple && 'space-between'};
	gap: 16px;
`;

const StyledTagsContainer = styled.div`
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
	justify-content: flex-start;
`;

const StyledDiscountContainer = styled.div`
	padding: 3px 5px;
	background-color: #ff3d3d;
	border-radius: 6px;

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: #ffffff;
	}
`;

const StyledDiscountTimerContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	height: 20px;
	padding: 0 5px;
	background-color: #ff3d3d;
	border-radius: 6px;

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: #ffffff;
	}

	.divider {
		width: 1px;
		height: 20px;
		background-color: rgba(0, 0, 0, 0.1);
	}
`;

const StyledTag = styled.div<ICaseTagStyled>`
	padding: 3px 5px;
	background: ${(props) => props.$bgColorNormal};
	border-radius: 6px;

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: ${(props) => props.$textColorNormal};
	}
`;

const StyledTimerContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 3px 5px;
	background: #ffffff;
	border-radius: 6px;
	transition: background-color 0.3s ease;

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: #000000;
		transition: color 0.3s ease;
	}
`;

const StyledBottomContainer = styled.div`
	position: relative;
	z-index: 2;

	${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;
		white-space: nowrap;
	}
`;

const StyledPriceContainer = styled.div`
	position: relative;
	display: flex;
	gap: 10px;
	justify-content: flex-start;
	align-items: center;
	margin: 12px 0 0;
	transition: margin 0.3s ease;
`;

const StyledPrice = styled.div<{ $isDonate?: boolean }>`
	display: flex;
	gap: 10px;
	align-items: center;

	img {
		transition: filter 0.3s ease;
	}

	${Text} {
		font-weight: 700;
		font-size: 20px;
		color: ${(props) => (props.$isDonate ? '#FFBB00' : '#ffffff')};
		padding-top: 1px;
		transition: color 0.3s ease;
	}
`;

const StyledOldPrice = styled.div`
	position: relative;
	display: flex;
	gap: 6px;
	padding: 0 5px;
	margin-top: 2px;

	img {
		width: 12px;
		height: 12px;
		margin-top: 2px;
		opacity: 0.25;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;
		opacity: 0.25;
		transition: color 0.3s ease;
	}

	.line {
		position: absolute;
		top: 7px;
		left: 0;
		width: 100%;
		height: 1px;
		background-color: #ff3737;
	}
`;

const StyledImageGradient = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-image: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, #000000 100%),
		linear-gradient(360deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
	background-size:
		auto 100px,
		auto 120px;
	background-repeat: repeat-x, repeat-x;
	background-position:
		0 0,
		0 100%;
	transition: filter 0.3s ease;
`;

const StyledCaseShadowItem = styled.div`
	position: absolute;
	bottom: 1px;
	left: 0;
	width: 100%;
	height: 180px;
	background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
`;

const StyledMoreBlock = styled.div`
	align-self: flex-start;
	width: fit-content;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	margin-bottom: -3px;
	height: 0;
	overflow: hidden;
	transition: height 0.3s ease;

	${Text} {
		font-weight: 700;
		font-size: 14px;
		color: #ffffff;
		opacity: 0.5;
		transition: opacity 0.3s ease;
	}

	img {
		width: 10px;
		height: 10px;
		user-select: none;
		pointer-events: none;
		opacity: 0.5;
		transition: opacity 0.3s ease;
	}

	&:hover ${Text}, &:hover img {
		opacity: 1;
	}
`;

const StyledCaseImageContainer = styled.div`
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 180px;
	height: 180px;
	user-select: none;
	pointer-events: none;
	transition:
		width 0.3s ease,
		height 0.3s ease,
		top 0.3s ease;

	img {
		transition:
			width 0.3s ease,
			height 0.3s ease;
	}
`;

const StyledImage = styled.img<{ $type?: CaseType }>`
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 180px;
	height: 180px;
	user-select: none;
	pointer-events: none;
	transition:
		width 0.3s ease,
		height 0.3s ease,
		top 0.3s ease;

	${(props) =>
		props.$type === CaseType.Kit &&
		css`
			width: 100%;
			height: 100%;
			object-fit: cover;
		`}
`;

const StyledKitImage = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	mask-image: linear-gradient(
		270deg,
		#ffffff 0%,
		rgba(255, 255, 255, 0) 83.44%
	);
	mask-size: 100% 100%;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: linear-gradient(
		270deg,
		#ffffff 0%,
		rgba(255, 255, 255, 0) 83.44%
	);
	-webkit-mask-size: 100% 100%;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	opacity: 0.5;
	transition: opacity 0.3s ease;

	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const StyledShadow = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 140px;
	background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
	opacity: 0.5;
	transition: height 0.3s ease;
`;

const StyledContainer = styled.div<{
	$type?: CaseType;
	$span?: number;
}>`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	min-width: 290px;
	width: 100%;
	height: 240px;
	padding: 19px 19px 18px 19px;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 5px;
	overflow: hidden;
	cursor: pointer;
	transition:
		background 0.3s ease,
		border 0.3s ease;

	@media (max-width: 1500px) {
		min-width: 250px;
	}

	${(props) =>
		props.$type === CaseType.Kit &&
		css`
			grid-column: span ${props.$span};
		`}

	${Spinner} {
		transition: filter 0.3s ease;
	}

	&:hover {
		${StyledMoreBlock} {
			height: 12px;
		}

		${StyledPriceContainer} {
			margin: 14px 0 17px;
		}

		${StyledKitImage} {
			opacity: 1;
		}

		${StyledShadow} {
			height: 180px;
		}

		${(props) =>
			props.$type === CaseType.Kit
				? css`
						border: 1px solid rgba(255, 255, 255, 1);
				  `
				: css`
						border: 1px solid rgba(255, 255, 255, 1);

						${StyledCaseShadowItem} {
							opacity: 0.9;
						}

						${StyledCaseImageContainer} {
							top: -20px;
							width: 260px;
							height: 260px;

							img {
								width: 260px;
								height: 260px;
							}
						}
				  `}
	}
`;

const StyledBackgroundSvg = styled.svg`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 330px;
	height: 270px;
`;

const StyledBg = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	mask-image: url(${bg});
	mask-size: cover;
	mask-position: center;
	mask-repeat: no-repeat;
	-webkit-mask-image: url(${bg});
	-webkit-mask-size: cover;
	-webkit-mask-position: center;
	-webkit-mask-repeat: no-repeat;
	background-color: #ffffff;
`;

const StyledSpinnerContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

export {
	StyledBackgroundSvg,
	StyledBg,
	StyledBottomContainer,
	StyledCaseImageContainer,
	StyledCaseShadowItem,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledImage,
	StyledImageGradient,
	StyledKitImage,
	StyledMoreBlock,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledSpinnerContainer,
	StyledTag,
	StyledTagsContainer,
	StyledTimerContainer,
	StyledTopContainer,
};
